export enum ReactFramework {
  NEXT_JS = "NEXT_JS",
  NEXT_JS_APP_ROUTER = "NEXT_JS_APP_ROUTER",
  NEXT_JS_TURBOPACK = "NEXT_JS_TURBOPACK",
  VITE = "VITE",
  VITE_REACT_ROUTER_V7 = "VITE_REACT_ROUTER_V7",
  CRA = "CRA",
  WEBPACK_DEV_SERVER = "WEBPACK_DEV_SERVER",
  REMIX = "REMIX",
  VITE_WITH_VIKE = "VITE_WITH_VIKE",
  EXPO = "EXPO",
  JUTRO = "JUTRO",
}

export const Language = {
  Javascript: "Javascript",
  Typescript: "Typescript",
} as const;
export type Language = (typeof Language)[keyof typeof Language];

export enum UiLibrary {
  SHADCN = "SHADCN",
  MUI = "MUI",
  JUST_TAILWIND = "JUST_TAILWIND",
}

export enum ProjectRunMode {
  CLOUD = "CLOUD",
}

export enum TemplateStack {
  SUPABASE = "SUPABASE",
  SUPABASE_POLAR = "SUPABASE_POLAR",
  SUPABASE_STRIPE = "SUPABASE_STRIPE",
  CLERK_CONVEX = "CLERK_CONVEX",
  CLERK_CONVEX_POLAR = "CLERK_CONVEX_POLAR",
  CLERK_CONVEX_STRIPE = "CLERK_CONVEX_STRIPE",
  BLANK = "BLANK",
  SLDS = "SLDS",
  QUILL = "QUILL",
  PERRYWEATHER = "PERRYWEATHER",
  GUIDEWIRE = "GUIDEWIRE",
}

export enum DesignSystem {
  BLANK = "BLANK",
  SLDS = "@salesforce/design-system-react",
  QUILL = "@deriv-com/quill-ui",
  PERRYWEATHER = "@/perry-weather",
  GUIDEWIRE = "@jutro/components",
}

export const CustomDesignSystems = [
  {
    importName: DesignSystem.PERRYWEATHER,
    title: "Vite with Perry Weather Design System",
    repo: "TempoLabsAI/vite-perry-weather",
    templateStack: TemplateStack.PERRYWEATHER,
    icon: "/images/design-system/perryweather.svg",
    featureFlag: "pw-design-system",
    framework: ReactFramework.VITE,
  },
  {
    importName: DesignSystem.QUILL,
    title: "Vite with Quill Design System",
    repo: "TempoLabsAI/vite-quill",
    templateStack: TemplateStack.QUILL,
    icon: "/images/design-system/quill.png",
    featureFlag: "quill-design-system",
    framework: ReactFramework.VITE,
  },
  {
    importName: DesignSystem.SLDS,
    title: "Vite with Lightning Design System",
    repo: "TempoLabsAI/vite-lightning",
    templateStack: TemplateStack.SLDS,
    icon: "/images/design-system/salesforce.svg",
    featureFlag: "lightning-design-system",
    framework: ReactFramework.VITE,
  },
  {
    importName: DesignSystem.GUIDEWIRE,
    title: "Next with Guidewire Jutro Design System",
    repo: "TempoLabsAI/next-guidewire",
    templateStack: TemplateStack.GUIDEWIRE,
    icon: "/images/design-system/guidewire.svg",
    featureFlag: "jutro-design-system",
    framework: ReactFramework.NEXT_JS, // will be updated to NEXT_JS_APP_ROUTER during project creation
  },
];

export const StackMintlifyLinkMapping = {
  [ReactFramework.VITE]: {
    [TemplateStack.SUPABASE_POLAR]:
      "https://tempolabsinc.mintlify.app/ViteSupabasePolar",
    [TemplateStack.SUPABASE_STRIPE]:
      "https://tempolabsinc.mintlify.app/ViteSupabaseStripe",
    [TemplateStack.CLERK_CONVEX_POLAR]:
      "https://tempolabsinc.mintlify.app/ViteClerkConvexPolar",
    [TemplateStack.CLERK_CONVEX_STRIPE]:
      "https://tempolabsinc.mintlify.app/ViteClerkConvexStripe",
  },
  [ReactFramework.NEXT_JS]: {
    [TemplateStack.SUPABASE_POLAR]:
      "https://tempolabsinc.mintlify.app/Next.jsSupabasePolar",
    [TemplateStack.SUPABASE_STRIPE]:
      "https://tempolabsinc.mintlify.app/Next.jsSupabaseStripe",
  },
};

// Repository paths for template projects
export const TEMPLATE_PROJECT_PATHS = [
  "TempoLabsAI/starter",
  "TempoLabsAI/starter-vite",
  "TempoLabsAI/starter-vite-swc",
  "TempoLabsAI/starter-app-router",
  "TempoLabsAI/next-swc",
  "TempoLabsAI/next-swc-turbopack",
  "TempoLabsAI/babel-starter",
  "TempoLabsAI/tutorial",
  "TempoLabsAI/tutorial-vite",
  "TempoLabsAI/next-swc-s",
  "TempoLabsAI/next-swc-sp",
  "TempoLabsAI/next-swc-ss",
  "TempoLabsAI/react-swc-s",
  "TempoLabsAI/react-swc-ss",
  "TempoLabsAI/react-swc-sp",
  "TempoLabsAI/react-swc-cc",
  "TempoLabsAI/react-swc-ccp",
  "TempoLabsAI/react-swc-ccs",
  "kevin1michael/shadcn-next13-starter",
  "kevin1michael/instagram-tutorial",
  "petergok/nextjs-mui-template",
  "petergok/nextjs-template",
  "TempoLabsAI/expo-template",
  "TempoLabsAI/vite-lightning",
  "TempoLabsAI/vite-perry-weather",
  "TempoLabsAI/next-guidewire",
  "TempoLabsAI/disconnected",
  "TempoLabsAI/starter-vite-react-router-7",
  "TempoLabsAI/tempo-tutorial-canvas",
].map((pt) => pt.toLowerCase());

export type TemplateConfig = {
  nodeVersion: string;
  packageManager: string;
  projectName: string;
  repoPath: string;
  framework: ReactFramework;
  language: Language;
};

// Template configurations by stack and framework
export const TEMPLATE_CONFIGS = {
  [TemplateStack.CLERK_CONVEX]: {
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC Clerk Convex",
      repoPath: "TempoLabsAI/react-swc-cc",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  [TemplateStack.CLERK_CONVEX_STRIPE]: {
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC CCS",
      repoPath: "TempoLabsAI/react-swc-ccs",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  [TemplateStack.CLERK_CONVEX_POLAR]: {
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC CCP",
      repoPath: "TempoLabsAI/react-swc-ccp",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  [TemplateStack.SUPABASE]: {
    [ReactFramework.NEXT_JS]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "Next.js SWC Supabase",
      repoPath: "TempoLabsAI/next-swc-s",
      framework: ReactFramework.NEXT_JS_APP_ROUTER,
      language: Language.Typescript,
    },
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC Supabase",
      repoPath: "TempoLabsAI/react-swc-s",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  [TemplateStack.SUPABASE_POLAR]: {
    [ReactFramework.NEXT_JS]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "Next.js SWC Supabase",
      repoPath: "TempoLabsAI/next-swc-sp",
      framework: ReactFramework.NEXT_JS_APP_ROUTER,
      language: Language.Typescript,
    },
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC Supabase",
      repoPath: "TempoLabsAI/react-swc-sp",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  [TemplateStack.SUPABASE_STRIPE]: {
    [ReactFramework.NEXT_JS]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "Next.js SWC Stripe",
      repoPath: "TempoLabsAI/next-swc-ss",
      framework: ReactFramework.NEXT_JS_APP_ROUTER,
      language: Language.Typescript,
    },
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "bun",
      projectName: "React SWC Stripe",
      repoPath: "TempoLabsAI/react-swc-ss",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
  },
  ...CustomDesignSystems.reduce(
    (acc, designSystem) => {
      acc[designSystem.templateStack] = {
        [designSystem.framework]: {
          nodeVersion: "20",
          packageManager: "npm",
          projectName: designSystem.title,
          repoPath: designSystem.repo,
          framework: designSystem.framework,
          language: Language.Typescript,
        },
      };
      return acc;
    },
    {} as Record<string, any>,
  ),
  default: {
    [ReactFramework.VITE]: {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "React SWC Starter",
      repoPath: "TempoLabsAI/starter-vite-swc",
      framework: ReactFramework.VITE,
      language: Language.Typescript,
    },
    [ReactFramework.VITE_REACT_ROUTER_V7]: {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "React SWC Starter",
      repoPath: "TempoLabsAI/starter-vite-react-router-7",
      framework: ReactFramework.VITE_REACT_ROUTER_V7,
      language: Language.Typescript,
    },
    [ReactFramework.NEXT_JS_TURBOPACK]: {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "Next.js Turbopack",
      repoPath: "TempoLabsAI/next-swc-turbopack",
      framework: ReactFramework.NEXT_JS,
      language: Language.Typescript,
    },
    [ReactFramework.NEXT_JS]: {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "Next.js SWC Starter",
      repoPath: "TempoLabsAI/next-swc",
      framework: ReactFramework.NEXT_JS_APP_ROUTER,
      language: Language.Typescript,
    },
    [ReactFramework.EXPO]: {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "Expo Starter",
      repoPath: "TempoLabsAI/expo-template",
      framework: ReactFramework.EXPO,
      language: Language.Typescript,
    },
  },
} as const;

/**
 * Gets the appropriate template based on the React framework and stack
 */
export const getTemplateForInputs = (
  reactFramework: ReactFramework,
  selectedTemplateStack: TemplateStack,
  useReactRouterV7?: boolean,
  useNextTurbopack?: boolean,
): TemplateConfig => {
  if (reactFramework === ReactFramework.EXPO) {
    // For now, no stacks for Expo
    return TEMPLATE_CONFIGS.default[reactFramework];
  }

  if (
    reactFramework === ReactFramework.VITE &&
    useReactRouterV7 &&
    selectedTemplateStack === TemplateStack.BLANK
  ) {
    return TEMPLATE_CONFIGS.default[ReactFramework.VITE_REACT_ROUTER_V7];
  }

  if (
    reactFramework === ReactFramework.NEXT_JS &&
    useNextTurbopack &&
    selectedTemplateStack === TemplateStack.BLANK
  ) {
    return TEMPLATE_CONFIGS.default[ReactFramework.NEXT_JS_TURBOPACK];
  }

  if (selectedTemplateStack === TemplateStack.GUIDEWIRE) {
    // force next js app router
    return {
      nodeVersion: "20",
      packageManager: "npm",
      projectName: "Next with Guidewire Jutro Design System",
      repoPath: "TempoLabsAI/next-guidewire",
      framework: ReactFramework.NEXT_JS_APP_ROUTER,
      language: Language.Typescript,
    };
  }

  // Get stack config or default if not found
  const stackConfig =
    TEMPLATE_CONFIGS[selectedTemplateStack] || TEMPLATE_CONFIGS.default;

  // Get framework-specific config or default for the stack
  const templateConfig =
    stackConfig[reactFramework] || stackConfig[ReactFramework.VITE];

  // Merge with base template
  return { ...templateConfig };
};

export const isValidConfiguration = (params: {
  reactFramework: ReactFramework;
  selectedTemplateStack: TemplateStack;
}) => {
  const paramsFramework = params.reactFramework;
  const selectedTemplateStack =
    params.selectedTemplateStack === TemplateStack.BLANK
      ? ("default" as const)
      : params.selectedTemplateStack;

  return TEMPLATE_CONFIGS[selectedTemplateStack][paramsFramework] !== undefined;
};
